/**
 * Asset Classes Hooks (Sprint C15-C WP E-3)
 *
 * React Query hooks for the Aaim canonical asset taxonomy. Backs the
 * platform-admin Asset Class Defaults UI. Types derived from
 * platform-admin.openapi.yaml via @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  assetClassesApi,
  type AssetClass,
  type AssetClassCreate,
  type AssetClassUpdate,
  type AssetClassListResponse,
} from '../api/client';

const QUERY_KEY = 'asset-classes';

export function useAssetClasses(params?: {
  groupCode?: string;
  uccClassification?: string;
  search?: string;
}) {
  return useQuery<AssetClassListResponse, Error>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => assetClassesApi.list(params),
  });
}

export function useAssetClass(assetClassCode: string | undefined) {
  return useQuery<AssetClass, Error>({
    queryKey: [QUERY_KEY, 'detail', assetClassCode],
    queryFn: () => {
      if (!assetClassCode) {
        throw new Error('assetClassCode is required');
      }
      return assetClassesApi.get(assetClassCode);
    },
    enabled: Boolean(assetClassCode),
  });
}

export function useCreateAssetClass() {
  const queryClient = useQueryClient();
  return useMutation<AssetClass, Error, AssetClassCreate>({
    mutationFn: (body) => assetClassesApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateAssetClass() {
  const queryClient = useQueryClient();
  return useMutation<
    AssetClass,
    Error,
    { assetClassCode: string; body: AssetClassUpdate }
  >({
    mutationFn: ({ assetClassCode, body }) =>
      assetClassesApi.update(assetClassCode, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'detail', variables.assetClassCode],
      });
    },
  });
}

export function useDeleteAssetClass() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (assetClassCode) => assetClassesApi.delete(assetClassCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
